import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { loadBudget } from 'loot-core/client/actions';

import { useActions } from '../../hooks/useActions';
import { useAuth } from '../../hooks/useAuth';
import { useDemoSite } from '../../hooks/useDemoSite';
import { AsyncStorageKey, useStorage } from '../../hooks/useStorage';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { tokens } from '../../tokens';
import { ExposeNavigate } from '../../util/router-tools';
import { Bootstrap } from '../auth/Bootstrap';
import ForgotPassword from '../auth/login/ForgotPassword';
import { Login } from '../auth/login/Login';
import { RemoteSignup } from '../auth/remote-signup/RemoteSignup';
import ResetPassword from '../auth/reset-password/ResetPassword';
import { Billing } from '../billing/Billing';
import { Text } from '../common/Text';
import { View } from '../common/View';
import { DemoBudgetDateLimit } from '../DemoBudgetDateLimit';
import { Notifications } from '../Notifications';
import { PostDemo } from '../PostDemo';
import { useServerVersion } from '../ServerContext';
import StartDemo from '../StartDemo';

import { BudgetList } from './BudgetList';
import { Modals } from './Modals';
import OnboardingAI from './onboarding/ai/OnboardingAI';
import { OnboardingMintMapping } from './onboarding/mint/OnboardingMintMapping';
import { OnboardingPaid } from './onboarding/OnboardingPaid';
import { OnboardingSteps } from './onboarding/steps/OnboardingSteps';
import StartModeScreen from './StartModeScreen';
import { Error } from './subscribe/Error';
import { OnboardingFree } from './onboarding/OnboardingFree';
import { Onboarding } from './onboarding/Onboarding';
import { AuthCallbackGoogle } from '../auth/AuthCallbackGoogle';

function Version() {
  const version = useServerVersion();

  return (
    <Text
      style={{
        color: theme.pageTextSubdued,
        ':hover': { color: theme.pageText },
        margin: 15,
        marginLeft: 17,
        [`@media (min-width: ${tokens.breakpoint_small})`]: {
          position: 'absolute',
          bottom: 0,
          right: 0,
          marginLeft: 15,
          marginRight: 17,
          zIndex: 5001,
        },
      }}
    >
      {`App: v${window.Actual?.ACTUAL_VERSION} | Server: ${version}`}
    </Text>
  );
}

export function ManagementApp({ isLoading }) {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const { isNarrowWidth } = useResponsive();

  const files = useSelector(state => state.budgets.allFiles);
  const userData = useSelector(state => state.user.data);
  const managerHasInitialized = useSelector(
    state => state.app.managerHasInitialized,
  );
  const { setItem: setIsDemoBootstrapped, getItem: getIsDemoBootstrapped } =
    useStorage(AsyncStorageKey.IS_DEMO_BOOTSTRAPPED);
  const { createDemoBudget, isDemoSite, isDemoBudgetFile } = useDemoSite();
  const { setAppState, getUserData, loadAllFiles } = useActions();

  useEffect(() => {
    const initDemoBudget = async () => {
      const isDemoBootstrapped = await getIsDemoBootstrapped();
      if (isDemoSite) {
        if (isDemoBootstrapped && files && files[0]) {
          dispatch(loadBudget(files[0].id));
        } else {
          await createDemoBudget();
          await setIsDemoBootstrapped(true);
        }
      }
    };

    if (
      managerHasInitialized &&
      !isLoading &&
      !window.location.pathname.includes('/post-demo')
    ) {
      initDemoBudget();
    }
  }, [managerHasInitialized, isLoading]);

  // runs on mount only
  useEffect(() => {
    // An action may have been triggered from outside, and we don't
    // want to override its loading message so we only show the
    // initial loader if there isn't already a message

    // Remember: this component is remounted every time the user
    // closes a budget. That's why we keep `managerHasInitialized` in
    // redux so that it persists across renders. This will show the
    // loading spinner on first run, but never again since we'll have
    // a cached list of files and can show them
    if (!managerHasInitialized) {
      if (!isLoading) {
        setAppState({ loadingText: '' });
      }
    }

    async function fetchData() {
      const userData = await getUserData();
      if (userData) {
        await loadAllFiles();
      }

      // TODO: There is a race condition here. The user could perform an
      // action that starts loading in between where `isLoading`
      // was captured and this would clear it. We really only want to
      // ever clear the initial loading screen, so we need a "loading
      // id" of some kind.
      setAppState({
        managerHasInitialized: true,
        ...(!isLoading ? { loadingText: null } : null),
      });
    }

    fetchData();
  }, []);

  if (!managerHasInitialized) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  return (
    <BrowserRouter>
      <ExposeNavigate />
      <DemoBudgetDateLimit />
      <View
        style={{
          height: '100%',
          color: theme.pageText,
          minHeight: '-webkit-fill-available',
          overflowY: 'scroll',
        }}
      >
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 40,
            WebkitAppRegion: 'drag',
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: 40,
            right: 15,
          }}
        >
          <Notifications
            style={{
              position: 'relative',
              left: 'initial',
              right: 'initial',
            }}
          />
        </View>

        {managerHasInitialized && (
          <View
            style={{
              alignItems: 'center',
              bottom: 0,
              justifyContent: 'center',
              left: 0,
              padding: isNarrowWidth ? 8 : 20,
              position: 'absolute',
              right: 0,
              top: 0,
              height: '100%',
              minHeight: 'fit-content',
            }}
          >
            <Routes>
              <Route path="/bootstrap" element={<Bootstrap />} />
              {isAuthenticated && (
                <>
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/onboarding/pro" element={<OnboardingPaid />} />
                  <Route path="/onboarding/free" element={<OnboardingFree />} />
                  <Route
                    path="/onboarding/start-fresh"
                    element={<OnboardingSteps />}
                  />
                  <Route
                    path="/onboarding/mint-mapping"
                    element={<OnboardingMintMapping />}
                  />
                  <Route path="/onboarding/start" element={<OnboardingAI />} />
                  <Route path="/budgets" element={<BudgetList />} />
                  <Route path="/entry-mode" element={<StartModeScreen />} />
                  {!isDemoBudgetFile && (
                    <Route
                      path="/billing"
                      element={<Billing inManagementApp={true} />}
                    />
                  )}
                </>
              )}
              {!isAuthenticated && !(isDemoSite || isDemoBudgetFile) && (
                <>
                  <Route path="/signup" element={<RemoteSignup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/login/:method?" element={<Login />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/auth-callback/google" element={<AuthCallbackGoogle />} />
                </>
              )}
              <Route path="/post-demo" element={<PostDemo />} />
              <Route path="/start-demo" element={<StartDemo />} />
              <Route path="/error" element={<Error />} />
              <Route
                path="/*"
                element={
                  <Navigate
                    to="/bootstrap"
                    replace={!isAuthenticated && !isDemoSite}
                  />
                }
              />
            </Routes>
          </View>
        )}
        {/*Disabled this for Good Steward*/}
        {/*<Version />*/}
      </View>
      <Modals />
    </BrowserRouter>
  );
}
